import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { shade, rgba } from 'polished'
import { useTheme } from '@emotion/react'
import { Container, Section, SectionTitle } from 'chaoskit/src/components'
import { link } from 'chaoskit/src/assets/styles/utility'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { useRef } from 'react'
import { motion } from 'framer-motion'

import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import SlackSignUpForm from '~partials/slack/SlackSignUpForm'
import Data from '~components/Data'
import Link from '~components/Link'
import ButtonLink from '~components/ButtonLink'
import Image from '~components/Image'
import { ReactComponent as HeroSVG } from '~media/community/hero.svg'
import { ReactComponent as FailoverConfSVG } from '~media/community/failover-conf.svg'
import { ReactComponent as RunSVG } from '~media/community/run.svg'
import { textGradientUnderline, stickyIt } from '~styles/utility'
import { ReactComponent as SlackSymbolSVG } from '~media/logos/slack-symbol.svg'
import { ReactComponent as SlackSVG } from '~media/community/slack.svg'
import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitle, HeroTitleSub } from '~partials/Hero'
import useReveal from '~hooks/useReveal'

const CommunityHero = ({ data }) => {
  const theme = useTheme()

  return (
    <Hero
      compact
      contrast={false}
      css={{
        background: generateGradient({
          start: theme.color.panel.base,
          stop: theme.color.light.base,
          position: 'to bottom',
        }),
      }}
    >
      <HeroContent>
        <HomeHeroGrid>
          <HomeHeroGridImage>
            <HeroSVG />
          </HomeHeroGridImage>
          <HomeHeroGridContent>
            <HeroTitle
              size="large"
              css={{
                span: [textGradientUnderline(theme)],
              }}
            >
              <Data>{data.heading}</Data>
            </HeroTitle>
            <HeroTitleSub css={{ marginTop: theme.space.large }}>
              <Data>{data.blurb}</Data>
            </HeroTitleSub>
          </HomeHeroGridContent>
        </HomeHeroGrid>
      </HeroContent>
    </Hero>
  )
}

CommunityHero.propTypes = {
  data: PropTypes.object.isRequired,
}

const Slack = ({ data }) => {
  const theme = useTheme()
  const sectionRef = useRef()

  const { controls, variants } = useReveal({ ref: sectionRef })

  return (
    <Section
      animate={controls}
      variants={variants}
      initial="hidden"
      ref={sectionRef}
    >
      <Container>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridTemplateColumns: '1fr 2fr',
              gap: theme.space.xlarge,
            },
          }}
        >
          <div>
            <div
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              <SlackSymbolSVG
                style={{ height: 60 }}
                css={{
                  display: 'block',
                  marginBottom: theme.space.base,

                  [theme.mq.small__max]: {
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  },
                }}
              />
              <SectionTitle
                align={{ medium: 'left' }}
                as="h2"
                title={<Data>{data.heading}</Data>}
                css={{ marginBottom: `${theme.space.large}px !important` }}
              />
              <SlackSignUpForm label="Get invite" />
            </div>
          </div>
          <div>
            <div
              css={{
                display: 'grid',
                gap: theme.space.xlarge,
              }}
            >
              {data.items.map((item) => (
                <div key={item.heading}>
                  <SectionTitle
                    as="h2"
                    align={{ medium: 'left' }}
                    title={<Data>{item.heading}</Data>}
                    sub={<Data>{item.blurb}</Data>}
                  />
                </div>
              ))}
            </div>
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr)',

                [theme.mq.small__max]: {
                  textAlign: 'center',
                },
              }}
            >
              <SlackSVG
                css={{
                  maxWidth: 400,
                  width: '100%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  marginTop: theme.space.base,

                  [theme.mq.large]: {
                    maxWidth: 'none',
                    width: 650,
                  },
                }}
              />
            </div>
          </div>
        </div>
      </Container>
    </Section>
  )
}

Slack.propTypes = {
  data: PropTypes.object.isRequired,
}

const CommunityGrid = ({
  title,
  blurb,
  svg: SVGComponent,
  imageOverlap,
  cta,
  ...rest
}) => {
  const theme = useTheme()
  const ref = useRef()

  const { controls, variants } = useReveal({
    ref,
  })

  return (
    <Section
      ref={ref}
      animate={controls}
      variants={variants}
      initial="hidden"
      {...rest}
    >
      <Container>
        <div
          css={[
            {
              display: 'grid',
              alignItems: 'center',
              gap: theme.space.large,

              [theme.mq.small__max]: {
                textAlign: 'center',
              },

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                gap: theme.space.xlarge,
              },
            },
          ]}
        >
          <div
            css={{
              [theme.mq.medium]: [
                {
                  gridColumn: '1 / span 7',
                  gridRow: 1,
                  direction: 'rtl',
                },

                imageOverlap && {
                  gridColumn: '1 / span 8',
                },
              ],
            }}
          >
            <SVGComponent
              css={{
                width: '100%',
                maxWidth: 400,
                margin: '0 auto',

                [theme.mq.medium]: {
                  maxWidth: 'none',
                  width: 600,
                },
              }}
            />
          </div>

          <div
            css={{
              [theme.mq.medium]: {
                gridColumn: '7 / span 6',
                gridRow: 1,
              },
            }}
          >
            <SectionTitle
              as="h2"
              align={{ medium: 'left' }}
              title={<Data>{title}</Data>}
              sub={<Data>{blurb}</Data>}
            />
            <ButtonLink type="primary" url={cta.url}>
              {cta.label}
            </ButtonLink>
          </div>
        </div>
      </Container>
    </Section>
  )
}

CommunityGrid.propTypes = {
  blurb: PropTypes.string.isRequired,
  cta: PropTypes.object.isRequired,
  svg: PropTypes.any.isRequired,
  imageOverlap: PropTypes.bool,
  title: PropTypes.string.isRequired,
}

const generateFeaturedArticleShadow = (theme) => {
  const shadows = []

  for (let index = 0; index < 50; index += 1) {
    const value = index + 1

    shadows.push(
      `${value}px ${value}px 0 0 ${rgba(
        theme.color.panel.base,
        1 - value * 0.02
      )}`
    )
  }

  return shadows.join(',')
}

const FeaturedTutorial = ({ tutorial }) => {
  const theme = useTheme()
  const ref = useRef()

  const shadowColor = shade(0.1, theme.color.panel.dark)

  const { controls, variants } = useReveal({ ref })

  return (
    <motion.article
      animate={controls}
      variants={variants}
      initial="hidden"
      ref={ref}
    >
      <Link
        to={`${tutorial.fields.fullUrl}?utm_medium=website&utm_source=community&utm_campaign=community_page`}
        css={[
          link.reset(theme),
          {
            display: 'block',
            fontWeight: theme.fontWeight.bold,
            textTransform: 'uppercase',
          },
        ]}
      >
        <div
          css={{
            background: generateGradient({
              start: shade(0.05, theme.color.panel.dark),
              stop: theme.color.panel.dark,
              position: 'to bottom right',
            }),
            borderRadius: theme.borderRadius.large,
            padding: theme.space.small,
            boxShadow: `
              1px 0 0 ${shadowColor},
              2px 0 0 ${shadowColor}, 3px 0 0 ${shadowColor}, 4px 0 0 ${shadowColor},
              5px 0 0 ${shadowColor}, 6px 0 0 ${shadowColor}, 7px 0 0 ${shadowColor},
              8px 0 0 ${shadowColor}, 9px 0 0 ${shadowColor}, 10px 0 0 ${shadowColor},
              ${generateFeaturedArticleShadow(theme)}
            `,
            marginBottom: theme.space.base,
          }}
        >
          <Image
            src={tutorial.frontmatter.featuredImage}
            alt={tutorial.frontmatter.title}
            css={{
              borderRadius: theme.borderRadius.large,
            }}
          />
        </div>
        <span
          css={{
            position: 'relative',

            '&::after': {
              content: "''",
              display: 'block',
              height: 2,
              background: generateGradient({
                start: theme.brand.teal,
                stop: theme.color.primary.base,
              }),
              marginTop: theme.space.base,
              marginLeft: theme.space.large,
              marginRight: theme.space.large,
            },
          }}
        >
          {tutorial.frontmatter.title}
        </span>
      </Link>
    </motion.article>
  )
}

FeaturedTutorial.propTypes = {
  tutorial: PropTypes.object.isRequired,
}

const ChaosEngineeringExpert = ({ data }) => {
  const theme = useTheme()
  const sectionRef = useRef()
  const moreTutorialsRef = useRef()

  const { controls: sectionControls, variants: sectionVariants } = useReveal({
    ref: sectionRef,
  })
  const {
    controls: moreTutorialsControls,
    variants: moreTutorialsVariants,
  } = useReveal({ ref: moreTutorialsRef })

  return (
    <Section
      animate={sectionControls}
      variants={sectionVariants}
      initial="hidden"
      ref={sectionRef}
    >
      <Container>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: theme.space.xlarge,
            },
          }}
        >
          <div>
            <SectionTitle
              align={{ medium: 'left' }}
              as="h2"
              title={<Data>{data.heading}</Data>}
              sub={<Data>{data.blurb}</Data>}
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            />
          </div>
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,
              textAlign: 'center',
            }}
          >
            {data.tutorials.map(({ tutorial }) => (
              <FeaturedTutorial
                key={tutorial.frontmatter.title}
                tutorial={tutorial}
              />
            ))}
            <motion.aside
              animate={moreTutorialsControls}
              variants={moreTutorialsVariants}
              initial="hidden"
              ref={moreTutorialsRef}
            >
              <h2>Still want more?</h2>
              <ButtonLink
                type="primary"
                url="/community/tutorials/?utm_medium=website&utm_source=community&utm_campaign=community_page&utm_content=tutorials"
              >
                Explore all tutorials
              </ButtonLink>
            </motion.aside>
          </div>
        </div>
      </Container>
    </Section>
  )
}

ChaosEngineeringExpert.propTypes = {
  data: PropTypes.object.isRequired,
}

const Community = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => (
  <BaseLayout headerProps={{ contrast: false }} footerProps={{ hideCTA: true }}>
    <HelmetSEO title={pageData.title} description={pageData.description} />
    <CommunityHero data={pageData.hero} />
    <Slack data={pageData.slack} />
    {pageData.promo.show && (
      <CommunityGrid
        title={pageData.promo.heading}
        blurb={pageData.promo.blurb}
        svg={FailoverConfSVG}
        cta={{
          label: pageData.promo.cta.label,
          url: pageData.promo.cta.url,
        }}
        imageOverlap
      />
    )}
    <CommunityGrid
      title={pageData.run.heading}
      blurb={pageData.run.blurb}
      svg={RunSVG}
      cta={{
        label: pageData.run.cta.label,
        url: pageData.run.cta.url,
      }}
      css={{
        paddingTop: '0 !important',
      }}
    />
    <ChaosEngineeringExpert data={pageData.expert} />
  </BaseLayout>
)

Community.propTypes = {
  data: PropTypes.object,
}

export default Community

export const pageQuery = graphql`
  query CommunityPageData {
    pageData: file(
      name: { eq: "community" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
          }
          slack {
            heading
            items {
              heading
              blurb
            }
          }
          promo {
            show
            heading
            blurb
            cta {
              label
              url
            }
          }
          run {
            heading
            blurb
            cta {
              label
              url
            }
          }
          expert {
            heading
            blurb
            tutorials {
              tutorial {
                frontmatter {
                  title
                  featuredImage
                }
                fields {
                  fullUrl
                }
              }
            }
          }
        }
      }
    }
  }
`
